import {
  AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import DrawerMenu from 'components/DrawerMenu';
import Notification from 'components/Notification';
import {useState} from 'react';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import {URLS} from '../../../../../utils/constants/urls';
import Currency from '../../../../../utils/hooks/getCurrency';
import {getLineItemDrawerWidth} from '../../../../../utils/validators/HelperFunctions';
import {getReceivableTypeLabel} from '../AddReceivable/receivableUtils';

type ManageReceivableDrawerProps = {
  open: boolean;
  onClose: () => void;
  bookingId: number;
  settlementEntry: any;
  onSuccess: () => void;
};

export const ManageReceivableDrawer = ({
  open,
  onClose,
  bookingId,
  settlementEntry,
  onSuccess,
}: ManageReceivableDrawerProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [reversingId, setReversingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{display: boolean; severity: AlertColor | null; message: string}>({
    display: false,
    severity: null,
    message: '',
  });

  const receipts = settlementEntry?.receipts || [];
  const activeReceipts = receipts.filter((receipt: any) => !receipt.reversed);

  const handleReverse = async (receiptId: number) => {
    setReversingId(receiptId);
    try {
      await axiosInstance.post(
        URLS.reverseSettlementReceipt(bookingId, settlementEntry.id, receiptId),
        {reason: strings.receivable_reverse_defaultReason},
      );
      setMessage({display: true, severity: 'success', message: strings.common_save_message});
      onSuccess();
      onClose();
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error.response?.data?.error_description ||
          error.response?.data?.message ||
          strings.something_went_wrong,
      });
    } finally {
      setReversingId(null);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage((prev) => ({...prev, display: value}));
  };

  const settlementLabel = settlementEntry
    ? `${getReceivableTypeLabel(settlementEntry.type)} - ${settlementEntry.companyName || '—'}`
    : '';

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <DrawerMenu
        open={open}
        handleClose={onClose}
        closeButton
        flexColumn
        width={getLineItemDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        title={strings.receivable_manage_title}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {strings.receivable_settle_settlementLabel}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {settlementLabel}
            </Typography>
          </Box>

          <Typography variant="subtitle2" fontWeight={700}>
            {strings.receivable_manage_settlements}
          </Typography>

          {activeReceipts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {strings.receivable_manage_noActiveSettlements}
            </Typography>
          ) : (
            <TableContainer sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2}}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{bgcolor: '#fafafa'}}>
                    <TableCell>{strings.receivable_column_date}</TableCell>
                    <TableCell>{strings.receivable_settle_paymentMode}</TableCell>
                    <TableCell>{strings.receivable_settle_referenceNo}</TableCell>
                    <TableCell align="right">{strings.receivable_column_amount}</TableCell>
                    <TableCell align="center">{strings.paymentDetails_column_actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((receipt: any) => (
                    <TableRow key={receipt.id}>
                      <TableCell>{dayjs(receipt.paymentDate || receipt.createdAt).format('DD MMM, YYYY')}</TableCell>
                      <TableCell>{receipt.paymentMode}</TableCell>
                      <TableCell>{receipt.referenceNo || '—'}</TableCell>
                      <TableCell align="right">
                        {receipt.formattedAmount || Currency(receipt.amount)}
                      </TableCell>
                      <TableCell align="center">
                        {receipt.reversed ? (
                          <Chip label={strings.receivable_status_reversed} size="small" />
                        ) : (
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={reversingId === receipt.id}
                            onClick={() => handleReverse(receipt.id)}>
                            {reversingId === receipt.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              strings.receivable_button_reverse
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DrawerMenu>
    </>
  );
};
